<template>
  <div class="city-list">
    <div class="row text-center">
      <div class="col-xs-12" role="region" aria-label="City select">
        <h2 class="ff-bold">
          {{ $t('events.events_in') }}
          <span @click="openCitySelectionModal">
            {{ $t('events.nearby_you') }}
            <CaretDownIcon class="city-list__dropdown-icon" />
          </span>
        </h2>
      </div>

      <CityAndDateSelect
        id="CityList"
        :should-close-on-select="true"
        :is-visible="shouldShowCitySelectionModal"
        :available-cities="sortedCategories"
        :show-online="false"
        :show-search="false"
        :start-at="0"
        :should-persist-in-dom="true"
        :should-portal="false"
        :should-href="true"
        @close="() => closeCitySelectionModal('Close', 'Close city Selector')"
        @citySelected="(event) => closeCitySelectionModal('Select City', 'Select City City Selector', event)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { useCategoryStore } from '@/stores/category';
import { sendButtonClickedEvent } from '@/resources/tracking';

function sendTrackingEvent(label: string, buttonAction: string, citySelected: string = '') {
  sendButtonClickedEvent({
    label,
    buttonAction,
    citySelected
  });
}

const CityAndDateSelect = defineAsyncComponent(() => import('@/components/molecules/selects/CityAndDateSelect.vue'));
const CaretDownIcon = defineAsyncComponent(() => import('@/assets/icons/icon--caret-down.svg?component'));

const categoryStore = useCategoryStore();
const { categories } = storeToRefs(categoryStore);
const shouldShowCitySelectionModal = ref(false);
const highlightedCities = [
  'berlin',
  'hamburg',
  'koeln',
  'stuttgart',
  'muenchen',
  'frankfurt',
  'braunschweig',
  'duesseldorf',
  'leipzig',
  'nuernberg'
];

onMounted(() => {
  if (!categories.value.length) {
    categoryStore.fetchCategories();
  }
});

const sortedCategories = computed(() => {
  const sorted = [...categories.value]
    // sort highlighted cities to the top and keep order according to highlightedCities array
    .sort((a, b) => {
      if (highlightedCities.includes(a.slug) && highlightedCities.includes(b.slug)) {
        return highlightedCities.indexOf(a.slug) - highlightedCities.indexOf(b.slug);
      } else if (highlightedCities.includes(a.slug)) {
        return -1;
      } else if (highlightedCities.includes(b.slug)) {
        return 1;
      } else {
        return 0;
      }
    });

  sorted
    // insert a divider after the first highlightedCities
    .splice(highlightedCities.length, 0, {
      divider: true,
      includeInMenu: true,
      productCount: 1
    });

  return sorted;
});

function closeCitySelectionModal(label: string, buttonAction: string, event = null) {
  shouldShowCitySelectionModal.value = false;
  const citySlected = event?.slug ?? '';
  sendTrackingEvent(label, buttonAction, citySlected);
}

function openCitySelectionModal() {
  shouldShowCitySelectionModal.value = true;
  sendTrackingEvent('deiner Nähe', 'Open City Selector');
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/font-size.scss' as *;

.city-list {
  margin-top: 3rem;
  margin-bottom: 3rem;
  overflow: hidden;
  padding: 0 20px;

  &:empty {
    display: none;
  }

  @include bp(md) {
    margin-bottom: 6rem;
  }

  h2 {
    margin-bottom: 2rem;
    @include font-size(32px);

    @include bp(md) {
      @include font-size(40px);
      margin-bottom: 4rem;
    }

    span {
      color: var(--color-primary);
      text-decoration: underline;
      cursor: pointer;
    }
  }

  &__dropdown-icon {
    width: 24px;
    margin-left: 10px;
  }

  &:deep(.modal-content) {
    text-align: left;
  }
}

a {
  text-decoration: underline;
  font-weight: bold;
  margin-bottom: 1.5rem;
  display: block;
}
</style>
